import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Textarea } from "@threadws/jute";
import { useState, type FormEvent } from "react";
import Markdown from "react-markdown";
import type { AgentRun } from "../../api/generated";
import { agentRunsGetOptions, agentRunsGetQueryKey, taskCaptureCreateMutation } from "../../api/generated/@tanstack/react-query.gen";
import { CHANNELS } from "../../realtime/ablyClient";
import { useChannel } from "../../realtime/useChannel";

const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const isWorking = (run: AgentRun | undefined) => !run || run.status === "queued" || run.status === "running";

/**
 * A box to type things to do into. The task capture agent turns them into tasks in the
 * background; this follows its run live and shows its reply.
 */
export function TaskCapture() {
  const queryClient = useQueryClient();
  const [text, setText] = useState("");
  const [runId, setRunId] = useState<string | null>(null);
  const capture = useMutation({
    ...taskCaptureCreateMutation(),
    onSuccess: ({ runId }) => {
      setRunId(runId);
      setText("");
    },
  });
  const { data: run } = useQuery({
    ...agentRunsGetOptions({ path: { id: runId ?? "" } }),
    enabled: runId !== null,
    // Ably normally brings the result; this is in case the live connection drops.
    refetchInterval: (query) => (isWorking(query.state.data) ? 5_000 : false),
  });
  useChannel(CHANNELS.agentRuns, ({ data }) => {
    const updated = data as AgentRun;
    if (updated.id === runId) queryClient.setQueryData(agentRunsGetQueryKey({ path: { id: updated.id } }), updated);
  });

  const working = capture.isPending || (runId !== null && isWorking(run));
  const submit = (event?: FormEvent) => {
    event?.preventDefault();
    if (text.trim() && !working) capture.mutate({ body: { text, timeZone } });
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <Textarea
        label="What do you need to do?"
        placeholder="Book the dentist, finish the Q4 deck by Friday (~2h), done with the invoice…"
        helpText="Write it however you like. ⌘↵ to send."
        rows={3}
        value={text}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) submit();
        }}
      />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div aria-live="polite" className="min-w-0 flex-1 text-sm">
          {capture.error && <p className="text-danger">{capture.error.message}</p>}
          {runId && working && <p className="text-text-secondary motion-safe:animate-pulse">Sorting that out…</p>}
          {run?.status === "failed" && <p className="text-danger">That didn't work: {run.error}</p>}
          {run?.status === "succeeded" && (
            <div className="flex flex-col gap-2 text-text-primary [&_strong]:font-medium [&_ul]:list-disc [&_ul]:pl-5">
              <Markdown>{run.output ?? ""}</Markdown>
            </div>
          )}
        </div>
        <Button type="submit" disabled={working || !text.trim()} icon="">
          {working ? "Working…" : "Add tasks"}
        </Button>
      </div>
    </form>
  );
}
